"use client";
import { Suspense, useState, useEffect, useRef } from 'react';
import { z } from 'zod';
import Link from 'next/link';
import { getSupabaseClient } from '@/lib/db/supabaseClient';
import { ThemeToggle } from '@/components/ThemeToggle';
import { AnimatedBackground } from '@/components/AnimatedBackground';
import type { MaintenanceCheckResponse } from '@/app/api/maintenance/check/route';
import { translateError } from '@/lib/utils/errorTranslator';
import { useSearchParams } from 'next/navigation';

const Schema = z.object({ 
	email: z.string().email('Ogiltig e-postadress'), 
	password: z.string().min(6, 'Lösenordet måste vara minst 6 tecken') 
});
const WishlistSchema = z.object({ 
	email: z.string().email('Ogiltig e-postadress'),
	name: z.string().optional()
});

function SignupContent() {
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [oauthLoading, setOauthLoading] = useState<null | 'google' | 'azure'>(null);
	const [visible, setVisible] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [signupDisabled, setSignupDisabled] = useState(false);
	const [isTester, setIsTester] = useState(false);
	const [maintenanceMessage, setMaintenanceMessage] = useState('Vi är under utveckling. Följ med på vår resa!');
	const [wishlistEmail, setWishlistEmail] = useState('');
	const [wishlistName, setWishlistName] = useState('');
	const [wishlistLoading, setWishlistLoading] = useState(false);
	const [wishlistSuccess, setWishlistSuccess] = useState(false);
	const [signupSuccess, setSignupSuccess] = useState(false);
	const [checkingMaintenance, setCheckingMaintenance] = useState(true);
	const formRef = useRef<HTMLDivElement>(null);
	const searchParams = useSearchParams();
	const oauthErrorParam = searchParams.get('error');

	useEffect(() => {
		setVisible(true);
		checkMaintenanceMode();
	}, []);

	useEffect(() => {
		if (!oauthErrorParam) return;
		if (oauthErrorParam === 'oauth_email_exists') {
			setError('Det finns redan ett konto med den här e-postadressen. Logga in med ditt lösenord.');
		} else {
			setError('Inloggningen misslyckades. Försök igen.');
		}
	}, [oauthErrorParam]);

	async function handleOAuthSignIn(provider: 'google' | 'azure') {
		setError(null);
		setOauthLoading(provider);
		try {
			const supabase = getSupabaseClient();
			const redirectTo = `${window.location.origin}/auth/callback`;
			const { error } = await supabase.auth.signInWithOAuth({
				provider,
				options: { redirectTo },
			});
			if (error) throw error;
		} catch (e: any) {
			setError(translateError(e, 'sv'));
			setOauthLoading(null);
		}
	}

	async function checkMaintenanceMode(email?: string) {
		try {
			setCheckingMaintenance(true);
			const url = email 
				? `/api/maintenance/check?email=${encodeURIComponent(email)}`
				: '/api/maintenance/check';
			const response = await fetch(url, { cache: 'no-store' }); // Prevent caching
			if (!response.ok) {
				throw new Error(`HTTP ${response.status}`);
			}
			const data: MaintenanceCheckResponse = await response.json();
			setSignupDisabled(data.signupDisabled);
			setIsTester(data.isTester);
			setMaintenanceMessage(data.maintenanceMessage);
		} catch (e) {
			console.error('Error checking maintenance mode:', e);
			// Default to allowing signup on error
			setSignupDisabled(false);
		} finally {
			setCheckingMaintenance(false);
		}
	}

	async function addToWishlist(e: React.FormEvent) {
		e.preventDefault();
		setWishlistLoading(true);
		setError(null);
		
		try {
			const parsed = WishlistSchema.parse({
				email: wishlistEmail,
				name: wishlistName
			});
			
			const response = await fetch('/api/wishlist', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(parsed)
			});
			
			const data = await response.json();
			
			if (!response.ok) {
				throw new Error(data.error || 'Något gick fel');
			}
			
			setWishlistSuccess(true);
			setWishlistEmail('');
			setWishlistName('');
		} catch (e: any) {
			if (e instanceof z.ZodError) {
				setError(e.issues[0]?.message || 'Ogiltig e-postadress');
			} else {
				setError(e.message || 'Något gick fel');
			}
		} finally {
			setWishlistLoading(false);
		}
	}

	async function onSubmit(formData: FormData) {
		// If maintenance mode is on, prevent signup (testers should log in instead)
		if (signupDisabled) {
			setError('Registrering är för närvarande stängd. Om du har fått en inbjudan att testa, vänligen logga in istället.');
			return;
		}
		
		// Check maintenance mode again before submitting (double-check)
		const email = formData.get('email') as string;
		const response = await fetch(`/api/maintenance/check?email=${encodeURIComponent(email)}`, { cache: 'no-store' });
		
		if (!response.ok) {
			setError('Kunde inte kontrollera registreringsstatus. Försök igen.');
			setLoading(false);
			return;
		}
		
		const data: MaintenanceCheckResponse = await response.json();
		
		if (data.signupDisabled && !data.isTester) {
			setError('Registrering är för närvarande stängd. Om du har fått en inbjudan att testa, vänligen logga in istället.');
			return;
		}
		
		setError(null);
		setLoading(true);
		try {
			const parsed = Schema.parse({ email: formData.get('email'), password: formData.get('password') });
			
			// Call server-side signup API that sends custom email
			const response = await fetch('/api/auth/signup', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					email: parsed.email,
					password: parsed.password,
				}),
			});
			
			const result = await response.json();
			
			if (!response.ok) {
				throw new Error(result.error || 'Registrering misslyckades');
			}
			
			// Email confirmation required - show success message
			setError(null);
			setLoading(false);
			setSignupSuccess(true);
		} catch (e: any) {
			setError(translateError(e, 'sv'));
			setLoading(false);
		}
	}

	const showWishlist = signupDisabled; // Always show maintenance page when disabled

	if (checkingMaintenance) {
		return (
			<main className="min-h-screen flex flex-col items-center justify-center px-6 py-20 relative overflow-x-hidden">
				<AnimatedBackground />
				<div className="relative z-10">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
				</div>
			</main>
		);
	}

	return (
		<>
			{/* Structured Data for SEO */}
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{
					__html: JSON.stringify({
						'@context': 'https://schema.org',
						'@type': 'SoftwareApplication',
						name: 'Karrelo',
						applicationCategory: 'JobSearchApplication',
						operatingSystem: 'Web',
						offers: {
							'@type': 'Offer',
							price: '0',
							priceCurrency: 'SEK'
						},
						description: 'AI-driven jobbsökning med ESCO-baserad matchning och ATS-optimerade ansökningsbrev',
						featureList: [
							'AI-powered job matching',
							'ESCO-based profile matching',
							'ATS-friendly application letters',
							'Transparent match scoring',
							'Application tracking'
						],
						aggregateRating: {
							'@type': 'AggregateRating',
							ratingValue: '4.8',
							reviewCount: '150'
						}
					})
				}}
			/>
			<main className="min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 py-12 sm:py-20 relative overflow-x-hidden">
				<AnimatedBackground />
				
			<header className="relative z-10 w-full flex justify-between items-center p-4 sm:p-6 absolute top-0">
				<Link 
					href="/" 
					className={`transition-all duration-700 flex items-center gap-2 text-slate-700 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-400 font-medium ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}
					style={{ transitionDelay: '0.1s' }}
				>
					<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
					</svg>
					<span className="hidden sm:inline">Tillbaka till startsidan</span>
				</Link>
				<div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
					<ThemeToggle />
				</div>
			</header>

				{showWishlist ? (
					<div className="relative z-10 w-full max-w-5xl mx-auto px-4 sm:px-6">
						{/* Hero Section - Matching Homepage */}
						<div className={`text-center mb-8 sm:mb-12 transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
							<h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold mb-4 sm:mb-6">
								<span className="text-gradient bg-gradient-to-r from-primary-600 via-teal-500 to-primary-600 bg-clip-text text-transparent animate-gradient">
									Karrelo
								</span>
							</h1>
							<div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
								<p className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-700 dark:text-slate-300">
									Ett smartare sätt att söka jobb – och mycket mer.
								</p>
								<p className="text-base sm:text-lg md:text-xl font-medium text-slate-600 dark:text-slate-400">
									Byggt på 20+ års erfarenhet av svensk arbetsmarknad
								</p>
							</div>
							<p className="text-lg sm:text-xl md:text-2xl font-semibold text-primary-600 dark:text-primary-400 mb-4 sm:mb-6">
								Förstärkt av AI – inte ersatt av den.
							</p>
							<div className="text-base sm:text-lg md:text-xl text-slate-600 dark:text-slate-400 max-w-4xl mx-auto space-y-2 leading-relaxed">
								<p>Karrelo bygger på metoder som använts i verklig jobbcoachning i över två decennier.</p>
								<p>Nu förstärkta av AI för att ge dig färre ansökningar, bättre träffsäkerhet och ansökningar som faktiskt blir lästa.</p>
							</div>
							<p className="text-lg sm:text-xl font-medium text-slate-700 dark:text-slate-300 mt-6 sm:mt-8">
								{maintenanceMessage}
							</p>
						</div>

						{/* Features Section - Matching Homepage */}
						<div className={`card-glass rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-12 mb-6 sm:mb-8 transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: '0.2s' }}>
							<h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 dark:text-slate-100 mb-4 sm:mb-6 text-center">
								Varför välja Karrelo?
							</h2>
							
							<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mt-6 sm:mt-8">
								{[
									{
										icon: (
											<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
											</svg>
										),
										title: 'Hitta jobb som verkligen passar dig',
										description: 'AI som förstår vad du söker och matchar dig med jobb där du faktiskt har en chans.'
									},
									{
										icon: (
											<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
											</svg>
										),
										title: 'Ansökningsbrev som faktiskt läses',
										description: 'Automatiskt genererade ansökningsbrev optimerade för både ATS-system och rekryterare.'
									},
									{
										icon: (
											<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
											</svg>
										),
										title: 'Få konkreta förslag på förbättringar',
										description: 'Se exakt vad som saknas och få tips på hur du kan förbättra din profil för bättre matchningar.'
									},
									{
										icon: (
											<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
											</svg>
										),
										title: 'Transparent matchningspoäng',
										description: 'Förstå exakt varför ett jobb matchar dig med tydlig poängsättning och förklaringar.'
									},
									{
										icon: (
											<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
											</svg>
										),
										title: 'Håll koll på alla ansökningar',
										description: 'Organisera och spåra alla dina ansökningar på ett enkelt sätt med vår tracker.'
									},
									{
										icon: (
											<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
											</svg>
										),
										title: 'Personliga rekommendationer',
										description: 'Få jobbrekommendationer skräddarsydda för just dig baserat på din unika profil.'
									}
								].map((feature, index) => (
									<div key={index} className="card-base rounded-xl sm:rounded-2xl p-4 sm:p-6 space-y-3 sm:space-y-4 hover-lift">
										<div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400 mb-3">
											{feature.icon}
										</div>
										<h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">{feature.title}</h3>
										<p className="text-slate-600 dark:text-slate-400 text-sm sm:text-base">{feature.description}</p>
									</div>
								))}
							</div>

							{/* Statistics */}
							<div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-slate-200 dark:border-slate-700">
								<div className="text-center">
									<div className="text-4xl md:text-5xl font-bold text-primary-600 dark:text-primary-400 mb-2">3x</div>
									<div className="text-lg font-semibold text-slate-800 dark:text-slate-200">Högre svarstakt</div>
									<div className="text-sm text-slate-600 dark:text-slate-400">Med färre men bättre ansökningar</div>
								</div>
								<div className="text-center">
									<div className="text-4xl md:text-5xl font-bold text-primary-600 dark:text-primary-400 mb-2">85%</div>
									<div className="text-lg font-semibold text-slate-800 dark:text-slate-200">Bättre matchning</div>
									<div className="text-sm text-slate-600 dark:text-slate-400">Tack vare AI-driven analys</div>
								</div>
								<div className="text-center">
									<div className="text-4xl md:text-5xl font-bold text-primary-600 dark:text-primary-400 mb-2">50%</div>
									<div className="text-lg font-semibold text-slate-800 dark:text-slate-200">Mindre tid</div>
									<div className="text-sm text-slate-600 dark:text-slate-400">På ansökningsprocessen</div>
								</div>
							</div>
						</div>

						{/* Wishlist Form */}
						<div
							ref={formRef}
							className={`card-glass rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-10 max-w-md mx-auto transition-all duration-1000 ${
								visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-95'
							}`}
							style={{ transitionDelay: '0.4s' }}
						>
							<h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 dark:text-slate-100 mb-3 sm:mb-4 text-center">
								Kommer snart!
							</h2>
							<p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4 sm:mb-6 text-center">
								Anmäl dig till vår väntelista och bli först med att prova Karrelo när vi lanserar.
							</p>
							
							{wishlistSuccess ? (
								<div className="p-3 sm:p-4 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800 text-center text-sm sm:text-base">
									<p>Tack! Vi meddelar dig när vi lanserar.</p>
								</div>
							) : (
								<form onSubmit={addToWishlist} className="space-y-3 sm:space-y-4">
									<div>
										<label htmlFor="wishlist-name" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
											Namn (valfritt)
										</label>
										<input
											id="wishlist-name"
											type="text"
											value={wishlistName}
											onChange={(e) => setWishlistName(e.target.value)}
											placeholder="Ditt namn"
											className="input-base w-full"
											aria-label="Namn"
										/>
									</div>
									<div>
										<label htmlFor="wishlist-email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
											E-post *
										</label>
										<input
											id="wishlist-email"
											type="email"
											inputMode="email"
											autoComplete="email"
											value={wishlistEmail}
											onChange={(e) => setWishlistEmail(e.target.value)}
											placeholder="din@epost.se"
											required
											className="input-base w-full"
											aria-label="E-postadress"
											aria-required="true"
										/>
									</div>
									<button
										type="submit"
										disabled={wishlistLoading}
										className="btn-primary w-full"
										aria-label="Anmäl intresse"
									>
										{wishlistLoading ? (
											<div className="flex items-center justify-center gap-2">
												<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
												<span>Skickar...</span>
											</div>
										) : 'Anmäl intresse'}
									</button>
								</form>
							)}
							
							{error && (
								<div className="mt-3 sm:mt-4 p-3 sm:p-4 rounded-lg bg-accent-error-50 dark:bg-accent-error-900/20 text-accent-error-700 dark:text-accent-error-400 border border-accent-error-200 dark:border-accent-error-800 text-sm sm:text-base" role="alert">
									{error}
								</div>
							)}
							
							{/* Tester invitation message */}
							<div className="mt-4 sm:mt-6 p-3 sm:p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
								<p className="text-xs sm:text-sm text-center text-blue-800 dark:text-blue-300 mb-2">
									<strong>Fick du en inbjudan att testa?</strong>
								</p>
								<p className="text-xs sm:text-sm text-center text-blue-700 dark:text-blue-400 mb-3">
									Om du har fått en inbjudan att testa Karrelo, vänligen logga in med ditt konto istället.
								</p>
								<div className="text-center">
									<a 
										href="/login" 
										className="inline-block px-4 sm:px-6 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg font-medium transition-colors text-sm sm:text-base min-h-[44px] flex items-center justify-center"
									>
										Logga in här
									</a>
								</div>
							</div>
							
							<p className="mt-4 sm:mt-6 text-xs sm:text-sm text-center text-slate-600 dark:text-slate-400">
								Har du redan ett konto?{' '}
								<a className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium underline underline-offset-2 transition-colors" href="/login">
									Logga in
								</a>
							</p>
						</div>
					</div>
				) : (
					<div
						ref={formRef}
						className={`relative z-10 card-glass rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-10 max-w-md w-full transition-all duration-1000 ${
							visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-95'
						}`}
						style={{ transitionDelay: '0.2s' }}
					>
						<h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 dark:text-slate-100 mb-6 sm:mb-8 text-center">
							Skapa konto
						</h1>
						{isTester && (
							<div className="mb-3 sm:mb-4 p-2 sm:p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800 text-xs sm:text-sm text-center">
								Du har teståtkomst! 🎉
							</div>
						)}
						{signupSuccess ? (
							<div className="p-3 sm:p-4 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800 text-center text-sm sm:text-base">
								<p className="font-semibold mb-2">Kontot har skapats!</p>
								<p>Ett bekräftelsemejl har skickats till din e-postadress. Vänligen klicka på länken i mejlet för att aktivera ditt konto.</p>
							</div>
						) : (
						<>
							<div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
								<button
									type="button"
									onClick={() => handleOAuthSignIn('google')}
									disabled={loading || oauthLoading !== null}
									className="w-full min-h-[44px] inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
								>
									{oauthLoading === 'google' ? (
										<div className="flex items-center gap-2">
											<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-700 dark:border-slate-200"></div>
											<span>Fortsätter…</span>
										</div>
									) : (
										<span className="flex items-center gap-2">
											<img src="/logos/google.svg" alt="" className="h-5 w-5" />
											<span>Fortsätt med Google</span>
										</span>
									)}
								</button>
								<button
									type="button"
									onClick={() => handleOAuthSignIn('azure')}
									disabled={loading || oauthLoading !== null}
									className="w-full min-h-[44px] inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
								>
									{oauthLoading === 'azure' ? (
										<div className="flex items-center gap-2">
											<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-700 dark:border-slate-200"></div>
											<span>Fortsätter…</span>
										</div>
									) : (
										<span className="flex items-center gap-2">
											<img src="/logos/microsoft.svg" alt="" className="h-5 w-5" />
											<span>Fortsätt med Microsoft</span>
										</span>
									)}
								</button>
								<div className="flex items-center gap-3">
									<div className="flex-1 h-px bg-slate-200 dark:bg-slate-700"></div>
									<span className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">eller</span>
									<div className="flex-1 h-px bg-slate-200 dark:bg-slate-700"></div>
								</div>
							</div>
							<form action={onSubmit} className="space-y-4 sm:space-y-6">
							<div className={`transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`} style={{ transitionDelay: '0.4s' }}>
								<label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">E-post</label>
								<input 
									id="email" 
									name="email" 
									type="email" 
									inputMode="email"
									autoComplete="email"
									placeholder="e‑post" 
									className="input-base w-full" 
									required 
									aria-required="true"
								/>
							</div>
							<div className={`transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`} style={{ transitionDelay: '0.5s' }}>
								<div className="flex items-center gap-2 mb-2">
									<label htmlFor="password" className="block text-sm font-medium text-slate-700 dark:text-slate-300">Lösenord</label>
									<div className="relative group">
										<button
											type="button"
											className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-400 focus:outline-none"
											aria-label="Lösenordskrav"
										>
											<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
											</svg>
										</button>
										<div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-slate-900 dark:bg-slate-800 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 pointer-events-none">
											<div className="font-medium mb-2">Lösenordskrav:</div>
											<ul className="space-y-1 text-slate-200">
												<li>• Minst 6 tecken</li>
												<li>• Gemener (a-z)</li>
												<li>• Versaler (A-Z)</li>
												<li>• Siffror (0-9)</li>
											</ul>
											<div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-900 dark:border-t-slate-800"></div>
										</div>
									</div>
								</div>
								<div className="relative">
									<input 
										id="password" 
										name="password" 
										type={showPassword ? "text" : "password"} 
										autoComplete="new-password"
										placeholder="lösenord" 
										className="input-base w-full pr-10" 
										required 
										aria-required="true" 
									/>
									<button
										type="button"
										onClick={() => setShowPassword(!showPassword)}
										className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded p-1"
										aria-label={showPassword ? "Dölj lösenord" : "Visa lösenord"}
									>
										{showPassword ? (
											<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.29 3.29m0 0L12 12m-5.71-5.71L3 3m9.29 9.29L21 21" />
											</svg>
										) : (
											<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
											</svg>
										)}
									</button>
								</div>
							</div>
							<div className={`transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`} style={{ transitionDelay: '0.6s' }}>
								<button disabled={loading} className="btn-primary w-full ripple hover:scale-105 transition-transform duration-300 shadow-lg hover:shadow-xl relative overflow-hidden group">
									<span className="relative z-10">
										{loading ? (
											<div className="flex items-center justify-center gap-2">
												<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
												<span>Skapar…</span>
											</div>
										) : 'Skapa konto'}
									</span>
									<div className="absolute inset-0 bg-gradient-to-r from-primary-700 to-teal-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
								</button>
							</div>
							</form>
						</>
						)}
						{error && (
							<div className={`mt-4 sm:mt-6 transition-all duration-500 ${error ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
								<p className="p-3 sm:p-4 rounded-lg bg-accent-error-50 dark:bg-accent-error-900/20 text-accent-error-700 dark:text-accent-error-400 border border-accent-error-200 dark:border-accent-error-800 text-sm sm:text-base" role="alert">
									{error}
								</p>
							</div>
						)}
						<p className={`mt-6 sm:mt-8 text-xs sm:text-sm text-center text-slate-600 dark:text-slate-400 transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`} style={{ transitionDelay: '0.7s' }}>
							Har du konto?{' '}
							<a className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium underline underline-offset-2 transition-colors" href="/login">
								Logga in
							</a>
						</p>
					</div>
				)}
			</main>
		</>
	);
}

export default function SignupPage() {
	return (
		<Suspense
			fallback={
				<main className="min-h-screen flex flex-col items-center justify-center px-6 py-20 relative overflow-x-hidden">
					<AnimatedBackground />
					<div className="relative z-10">
						<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
					</div>
				</main>
			}
		>
			<SignupContent />
		</Suspense>
	);
}
